import { configureStore } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import rootReducer from '../slices';
import { aprPortfolioApi } from '../api/aprPortfolio';
import { paymentsApi } from '../api/payments';
import { invoicesApi } from '../api/invoices';
import { vendorsApi } from '../api/vendors';
import { serviceAgreementsApi } from '../api/serviceAgreements';
import { partnerServiceAgreementsApi } from '../api/partnerServiceAgreements';
import { externalVendorsApi } from '../api/externalVendors';
import { dataReconciliationApi } from '../api/dataReconciliation';
import { permissionsApi } from '../api/permissions';
import { vendorCsvImportApi } from '../api/vendorCsvImport';
import { partnerRevSharesApi } from '../api/partnerRevShares';
import { partnersApi } from '../api/partners';
import { emailSequencesApi } from '../api/emailSequences';
import { emailTemplatesApi } from '../api/emailTemplates';
import { faqsApi } from '../api/faqs';
import { matchingCacheInvalidation } from '../middleware/matchingCacheInvalidation';
import { emailSequenceAuditCacheInvalidation } from '../middleware/emailSequenceAuditCacheInvalidation';

const persistConfig = {
  key: 'root',
  storage,
  whitelist: [
    'acceleratedPayout',
    'adjustments',
    'adjustmentGroups',
    'anomalies',
    'auth',
    'batchSweeps',
    'cohorts',
    'collections',
    'eligibleInvoices',
    'enablementV2',
    'vendorEmailSequences',
    'actionItems',
    'globalSearch',
    'invoices',
    'invoiceQuarantines',
    'invoiceLineItems',
    'vendorTransfers',
    'onboardings',
    'reconciliation',
    'vendors',
    'clients',
    'externalVendors',
    'facilitators',
    'kpis',
    'externalClients',
    'externalClientVendors',
    'invoiceAdjustmentApprovals',
    'revenue',
    'repaymentItems',
    'tasks',
    'partnerIntegrations',
    'programs',
    'vendorAccountUnderwriting',
    'scheduledVendorCollections',
    'serviceAgreements',
    'configurationSettings',
    'programPromotions',
    'factoringAccess',
    'transactionMatchExplorer',
    'partnerRevShares',
    'portfolioMonitor',
    'faqs',
  ],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const concatenatedMiddleware = [
  paymentsApi.middleware,
  invoicesApi.middleware,
  aprPortfolioApi.middleware,
  vendorsApi.middleware,
  serviceAgreementsApi.middleware,
  partnerServiceAgreementsApi.middleware,
  externalVendorsApi.middleware,
  permissionsApi.middleware,
  vendorCsvImportApi.middleware,
  dataReconciliationApi.middleware,
  partnerRevSharesApi.middleware,
  partnersApi.middleware,
  emailSequencesApi.middleware,
  emailTemplatesApi.middleware,
  faqsApi.middleware,
];

export const createConfiguredStore = () => {
  return configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      })
        // Runs after the api middleware so the fulfilled action has already been processed.
        .concat(concatenatedMiddleware)
        .concat(matchingCacheInvalidation.middleware)
        .concat(emailSequenceAuditCacheInvalidation.middleware)
  });
};

/** @TODO migration for new limit values */
export const persistor = persistStore(createConfiguredStore());

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const store = createConfiguredStore();
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
